import type { SingleSearchRequest, CollectabilityAPIResponse, SingleSearchResult, SearchType } from '@/types';
import { logger, ValidationError, NetworkError, ProcessingError, NoHitError } from '@/utils/logger';

const COLLECTABILITY_API_URL = import.meta.env.VITE_COLLECTABILITY_API_URL;
const COLLECTABILITY_API_KEY = import.meta.env.VITE_COLLECTABILITY_API_KEY;

if (!COLLECTABILITY_API_URL) {
  throw new Error('VITE_COLLECTABILITY_API_URL environment variable is required');
}

if (!COLLECTABILITY_API_KEY) {
  throw new Error('VITE_COLLECTABILITY_API_KEY environment variable is required');
}

/**
 * Request structure for CollectABILITY API
 */
interface CollectabilityAPIRequest {
  operation: SearchType;
  company_id: string;
  amount_to_collect: string;
  ai_overview: boolean;
  nameAddresses: Array<{
    firstName: string;
    middleName: string;
    lastName: string;
    fullName: string;
    address: {
      line1: string;
      city: string;
      region: string;
      postalCode: string;
      countryCode: string;
    };
  }>;
  emailAddresses: string[];
  phoneNumbers: string[];
}

/**
 * Service for calling the real CollectABILITY API
 */
class CollectabilityAPIService {
  /**
   * Transform API response to UI-friendly format
   * Handles all operation types: validate, enrich, enrich_validate, scoring, scoring_enrich
   */
  private transformResponse(
    apiResponse: CollectabilityAPIResponse,
    searchRequest: SingleSearchRequest
  ): SingleSearchResult {
    const searchId = `search_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const isScoringOperation = searchRequest.searchType === 'scoring' || searchRequest.searchType === 'scoring_enrich';
    const isEnrichOperation = searchRequest.searchType.includes('enrich');
    const isValidateOperation = searchRequest.searchType === 'validate' || searchRequest.searchType === 'enrich_validate';

    // Handle different response structures based on operation type
    let primaryVerifiedData: any;
    let primaryAddress: string;

    if (searchRequest.searchType === 'enrich' && !isScoringOperation) {
      // For pure enrich operation, data is in primaryVerified and enrichedData (no validationDetails)
      if (!apiResponse.primaryVerified || !apiResponse.enrichedData) {
        logger.error('Enrich operation missing expected fields', {
          hasPrimaryVerified: !!apiResponse.primaryVerified,
          hasEnrichedData: !!apiResponse.enrichedData,
          responseKeys: Object.keys(apiResponse),
        });
        throw new ProcessingError('Invalid API response for enrich operation - missing primaryVerified or enrichedData');
      }

      // Build primary address from primaryVerified
      primaryAddress = typeof apiResponse.primaryVerified.address === 'object'
        ? `${apiResponse.primaryVerified.address.street}, ${apiResponse.primaryVerified.address.city}, ${apiResponse.primaryVerified.address.state} ${apiResponse.primaryVerified.address.zip}`
        : apiResponse.primaryVerified.address || 'Not provided';

      primaryVerifiedData = {
        name: apiResponse.primaryVerified.name,
        nameStatus: undefined, // No validation status for pure enrich
        phone: apiResponse.primaryVerified.phone || null,
        phoneStatus: undefined,
        email: apiResponse.primaryVerified.email || null,
        emailStatus: undefined,
        address: primaryAddress,
        addressStatus: undefined,
        age: apiResponse.primaryVerified.age || null,
        dateOfBirth: apiResponse.primaryVerified.dateOfBirth || null,
        firstSeen: apiResponse.primaryVerified.firstSeen || null,
        lastSeen: apiResponse.primaryVerified.lastSeen || null,
        identityConfidenceScore: apiResponse.primaryVerified.identityConfidenceScore || 0,
        fraudRisk: apiResponse.primaryVerified.fraudRisk || 0,
      };
    } else if (isValidateOperation && !isScoringOperation) {
      // For pure validate or enrich_validate operations, data is in validationDetails and searchedPerson
      if (!apiResponse.validationDetails || !apiResponse.searchedPerson) {
        logger.error('Validate operation missing expected fields', {
          hasValidationDetails: !!apiResponse.validationDetails,
          hasSearchedPerson: !!apiResponse.searchedPerson,
          responseKeys: Object.keys(apiResponse),
        });
        throw new ProcessingError('Invalid API response for validate operation - missing validation data');
      }

      const valDetails = apiResponse.validationDetails;
      const searchedPerson = apiResponse.searchedPerson;
      const inputVal = valDetails.inputValidation;
      const fraudInsights = valDetails.fraudInsights;

      // For enrich_validate, use primaryVerified data which contains enriched info
      const usePrimaryVerified = isEnrichOperation && apiResponse.primaryVerified;

      // Build primary address
      if (usePrimaryVerified && apiResponse.primaryVerified && apiResponse.primaryVerified.address) {
        // Use enriched address from primaryVerified
        primaryAddress = typeof apiResponse.primaryVerified.address === 'object'
          ? `${apiResponse.primaryVerified.address.street}, ${apiResponse.primaryVerified.address.city}, ${apiResponse.primaryVerified.address.state} ${apiResponse.primaryVerified.address.zip}`
          : apiResponse.primaryVerified.address;
      } else if (inputVal.address?.standardized) {
        // Use validated input address
        const addrObj = inputVal.address.standardized;
        if (typeof addrObj === 'object' && addrObj !== null) {
          primaryAddress = `${addrObj.line1 || ''}${addrObj.line2 ? ' ' + addrObj.line2 : ''}, ${addrObj.city || ''}, ${addrObj.region || ''} ${addrObj.postalCode || ''}`.trim();
        } else {
          primaryAddress = String(addrObj);
        }
      } else {
        primaryAddress = searchedPerson.searchedAddress?.line1 || 'Not provided';
      }

      primaryVerifiedData = {
        name: inputVal.name?.standardized || searchedPerson.name,
        nameStatus: {
          validated: inputVal.name?.validated || false,
          primary: inputVal.name?.primary || false,
        },
        phone: (usePrimaryVerified && apiResponse.primaryVerified ? apiResponse.primaryVerified.phone : null) || inputVal.phone?.standardized || searchedPerson.searchedPhone || null,
        phoneStatus: {
          validated: inputVal.phone?.validated || false,
          primary: inputVal.phone?.primary || false,
        },
        email: (usePrimaryVerified && apiResponse.primaryVerified ? apiResponse.primaryVerified.email : null) || inputVal.email?.standardized || searchedPerson.searchedEmail || null,
        emailStatus: {
          validated: inputVal.email?.validated || false,
          primary: inputVal.email?.primary || false,
        },
        address: primaryAddress,
        addressStatus: {
          validated: inputVal.address?.validated || false,
          primary: inputVal.address?.primary || false,
        },
        age: usePrimaryVerified && apiResponse.primaryVerified ? apiResponse.primaryVerified.age : null,
        dateOfBirth: usePrimaryVerified && apiResponse.primaryVerified ? apiResponse.primaryVerified.dateOfBirth : null,
        firstSeen: fraudInsights?.firstSeen || null,
        lastSeen: fraudInsights?.lastSeen || null,
        identityConfidenceScore: fraudInsights?.identityConfidenceScore || 0,
        fraudRisk: fraudInsights?.fraudRisk || 0,
      };
    } else {
      // For scoring operations, data is in primaryVerified
      if (!apiResponse.primaryVerified) {
        logger.error('Scoring operation missing primaryVerified data', {
          searchType: searchRequest.searchType,
          responseKeys: Object.keys(apiResponse),
        });
        throw new ProcessingError('Invalid API response - missing primaryVerified data for scoring operation');
      }

      primaryAddress = typeof apiResponse.primaryVerified.address === 'object'
        ? `${apiResponse.primaryVerified.address.street}, ${apiResponse.primaryVerified.address.city}, ${apiResponse.primaryVerified.address.state} ${apiResponse.primaryVerified.address.zip}`
        : apiResponse.primaryVerified.address;

      // Scoring operations ALSO run Kount validation - extract validation status if available
      logger.info('Scoring operation - checking for validation data', {
        hasValidationDetails: !!apiResponse.validationDetails,
        hasInputValidation: !!apiResponse.validationDetails?.inputValidation,
        validationDetailsKeys: apiResponse.validationDetails ? Object.keys(apiResponse.validationDetails) : [],
      });

      const inputVal = apiResponse.validationDetails?.inputValidation;

      if (inputVal) {
        logger.info('Validation data found for scoring operation', {
          hasName: !!inputVal.name,
          hasPhone: !!inputVal.phone,
          hasEmail: !!inputVal.email,
          hasAddress: !!inputVal.address,
        });
      } else {
        logger.warn('No validation data found for scoring operation - will show "Not Verified" badges');
      }

      primaryVerifiedData = {
        name: apiResponse.primaryVerified.name,
        nameStatus: inputVal?.name ? {
          validated: inputVal.name.validated || false,
          primary: inputVal.name.primary || false,
        } : undefined,
        phone: apiResponse.primaryVerified.phone || null,
        phoneStatus: inputVal?.phone ? {
          validated: inputVal.phone.validated || false,
          primary: inputVal.phone.primary || false,
        } : undefined,
        email: apiResponse.primaryVerified.email || null,
        emailStatus: inputVal?.email ? {
          validated: inputVal.email.validated || false,
          primary: inputVal.email.primary || false,
        } : undefined,
        address: primaryAddress,
        addressStatus: inputVal?.address ? {
          validated: inputVal.address.validated || false,
          primary: inputVal.address.primary || false,
        } : undefined,
        age: apiResponse.primaryVerified.age,
        dateOfBirth: apiResponse.primaryVerified.dateOfBirth,
        firstSeen: apiResponse.primaryVerified.firstSeen,
        lastSeen: apiResponse.primaryVerified.lastSeen,
        identityConfidenceScore: apiResponse.primaryVerified.identityConfidenceScore,
        fraudRisk: apiResponse.primaryVerified.fraudRisk,
        trustScore: apiResponse.primaryVerified.trustScore,
      };
    }

    // Base result with primary verified data
    const result: SingleSearchResult = {
      id: searchId,
      searchRequest,
      timestamp: new Date().toISOString(),
      requestId: apiResponse.request_id,
      primaryVerified: primaryVerifiedData,
    };

    // Add scoring data if available (scoring operations only)
    if (isScoringOperation && apiResponse.scoringResult && apiResponse['IntelligentDebt Insights']) {
      const recoveryPlan = apiResponse.scoringResult.results.collectability_plan;
      const parseAmount = (str: string): number => parseFloat(str.replace(/[$,]/g, ''));
      const parsePercent = (str: string): number => parseFloat(str.replace(/%/g, ''));

      result.collectabilityScore = apiResponse.scoringResult.results.collectability_score;
      result.collectabilityBand = apiResponse.scoringResult.results.collectability_band;
      result.suspectedFraud = apiResponse.scoringResult.results.suspected_fraud;

      result.insights = {
        income360: apiResponse['IntelligentDebt Insights'].income360,
        affluenceIndex: apiResponse['IntelligentDebt Insights'].affluenceIndex,
        economicCohort: apiResponse['IntelligentDebt Insights'].economicCohorts,
        spendingPower: apiResponse['IntelligentDebt Insights'].spendingPower,
        financialDurabilityIndex: apiResponse['IntelligentDebt Insights'].financialDurabilityIndex,
      };

      result.recoveryScenarios = {
        optimized: [
          {
            name: 'Optimized Payment Plan',
            payment: parseAmount(recoveryPlan['Optimized Payment Plan']['$ Payment']),
            repaymentTerm: recoveryPlan['Optimized Payment Plan']['Repayment Term'],
            totalRecovered: parseAmount(recoveryPlan['Optimized Payment Plan']['Total $ Recovered']),
            probabilityRecovery: parsePercent(recoveryPlan['Optimized Payment Plan']['Pr(Recovery)']),
          },
          {
            name: 'Optimized Settlement Plan',
            payment: parseAmount(recoveryPlan['Optimized Settlement Plan']['$ Payment']),
            repaymentTerm: recoveryPlan['Optimized Settlement Plan']['Repayment Term'],
            totalRecovered: parseAmount(recoveryPlan['Optimized Settlement Plan']['Total $ Recovered']),
            probabilityRecovery: parsePercent(recoveryPlan['Optimized Settlement Plan']['Pr(Recovery)']),
          },
        ],
        low: [
          {
            name: 'Low $ Payment Plan',
            payment: parseAmount(recoveryPlan['Low $ Payment Plan']['$ Payment']),
            repaymentTerm: recoveryPlan['Low $ Payment Plan']['Repayment Term'],
            totalRecovered: parseAmount(recoveryPlan['Low $ Payment Plan']['Total $ Recovered']),
            probabilityRecovery: parsePercent(recoveryPlan['Low $ Payment Plan']['Pr(Recovery)']),
          },
          {
            name: 'Low $ Settlement Plan',
            payment: parseAmount(recoveryPlan['Low $ Settlement Plan']['$ Payment']),
            repaymentTerm: recoveryPlan['Low $ Settlement Plan']['Repayment Term'],
            totalRecovered: parseAmount(recoveryPlan['Low $ Settlement Plan']['Total $ Recovered']),
            probabilityRecovery: parsePercent(recoveryPlan['Low $ Settlement Plan']['Pr(Recovery)']),
          },
        ],
        high: [
          {
            name: 'High $ Payment Plan',
            payment: parseAmount(recoveryPlan['High $ Payment Plan']['$ Payment']),
            repaymentTerm: recoveryPlan['High $ Payment Plan']['Repayment Term'],
            totalRecovered: parseAmount(recoveryPlan['High $ Payment Plan']['Total $ Recovered']),
            probabilityRecovery: parsePercent(recoveryPlan['High $ Payment Plan']['Pr(Recovery)']),
          },
          {
            name: 'High $ Settlement Plan',
            payment: parseAmount(recoveryPlan['High $ Settlement Plan']['$ Payment']),
            repaymentTerm: recoveryPlan['High $ Settlement Plan']['Repayment Term'],
            totalRecovered: parseAmount(recoveryPlan['High $ Settlement Plan']['Total $ Recovered']),
            probabilityRecovery: parsePercent(recoveryPlan['High $ Settlement Plan']['Pr(Recovery)']),
          },
        ],
      };
    }

    // Add enriched data if available (enrich operations)
    if (isEnrichOperation && apiResponse.enrichedData && apiResponse.primaryVerified) {
      result.personalInfo = {
        fullName: apiResponse.primaryVerified.name,
        age: apiResponse.primaryVerified.age,
        dob: apiResponse.primaryVerified.dateOfBirth,
        addresses: apiResponse.enrichedData.addresses.map((addr) => ({
          street: addr.street,
          city: addr.city,
          state: addr.state,
          zip: addr.zip,
          unit: addr.unit,
          validated: addr.validated,
          primary: addr.primary,
        })),
        phones: apiResponse.enrichedData.phones.map((phone) => ({
          number: phone.number,
          type: phone.type,
          carrier: phone.carrier,
          isConnected: phone.isConnected,
          validated: phone.validated,
          primary: phone.primary,
          lastReportedDate: phone.lastReportedDate,
          isDNC: phone.isDNC,
        })),
        emails: apiResponse.enrichedData.emails.map((email) => ({
          address: email.address,
          isValidated: email.isValidated,
          primary: email.primary,
          isBusiness: email.isBusiness,
        })),
        relatives: apiResponse.associates?.relatives || [],
        employers: apiResponse.associates?.employers || [],
        primaryAddress,
        identityConfidenceScore: apiResponse.primaryVerified.identityConfidenceScore,
        fraudRisk: apiResponse.primaryVerified.fraudRisk,
        firstSeen: apiResponse.primaryVerified.firstSeen,
        lastSeen: apiResponse.primaryVerified.lastSeen,
      };
    }

    // Add financial profile if available (scoring_enrich only)
    if (apiResponse.financialProfile && searchRequest.searchType === 'scoring_enrich') {
      result.financialProfile = apiResponse.financialProfile;
    }

    // Add AI overview if available (Phase 2 - backend generates it)
    if (apiResponse.ai_overview) {
      result.aiOverview = apiResponse.ai_overview.overview;
      result.aiRiskAssessment = apiResponse.ai_overview.riskAssessment;
      result.aiRecommendedStrategy = apiResponse.ai_overview.recommendedStrategy;
    }

    return result;
  }

  /**
   * Perform a single person search
   * Supports all operation types: validate, enrich, enrich_validate, scoring, scoring_enrich
   */
  async performSearch(request: SingleSearchRequest): Promise<SingleSearchResult> {
    if (!request.firstName || !request.lastName) {
      throw new ValidationError('First name and last name are required', 'name');
    }

    const validSearchTypes: SearchType[] = ['validate', 'enrich', 'enrich_validate', 'scoring', 'scoring_enrich'];
    if (!request.searchType || !validSearchTypes.includes(request.searchType)) {
      throw new ValidationError('Invalid search type', 'searchType');
    }

    // Amount to collect is required for scoring operations only
    const isScoringOperation = request.searchType === 'scoring' || request.searchType === 'scoring_enrich';
    if (isScoringOperation && (!request.amountToCollect || request.amountToCollect <= 0)) {
      throw new ValidationError('Amount to collect is required for scoring operations', 'amountToCollect');
    }

    logger.info('Calling CollectABILITY API', {
      firstName: request.firstName,
      lastName: request.lastName,
      searchType: request.searchType,
      amountToCollect: request.amountToCollect,
    });

    // Build nameAddresses array - handle both single and multiple address entries
    const nameAddresses = [];

    // Check if using multiple entries (validate/scoring operations)
    if (request.addresses && request.addresses.length > 0) {
      // Multiple address entries
      request.addresses.forEach(addr => {
        if (addr.address && addr.address.trim().length > 0) {
          nameAddresses.push({
            firstName: request.firstName,
            middleName: '',
            lastName: request.lastName,
            fullName: '',
            address: {
              line1: addr.address || '',
              city: addr.city || '',
              region: addr.state || '',
              postalCode: addr.zip || '',
              countryCode: 'US',
            },
          });
        }
      });
    } else if (request.address && request.address.trim().length > 0) {
      // Single address entry (backward compatible for enrich operations)
      nameAddresses.push({
        firstName: request.firstName,
        middleName: '',
        lastName: request.lastName,
        fullName: '',
        address: {
          line1: request.address || '',
          city: request.city || '',
          region: request.state || '',
          postalCode: request.zip || '',
          countryCode: 'US',
        },
      });
    }

    // If no addresses provided at all, add entry with empty address
    if (nameAddresses.length === 0) {
      nameAddresses.push({
        firstName: request.firstName,
        middleName: '',
        lastName: request.lastName,
        fullName: '',
        address: {
          line1: '',
          city: '',
          region: '',
          postalCode: '',
          countryCode: 'US',
        },
      });
    }

    // Build email addresses array - handle both single and multiple entries
    let emailAddresses: string[] = [];
    if (request.emails && request.emails.length > 0) {
      // Multiple email entries - filter out empty strings
      emailAddresses = request.emails.filter(e => e && e.trim().length > 0);
    } else if (request.email && request.email.trim().length > 0) {
      // Single email entry
      emailAddresses = [request.email];
    }
    // If no emails, send empty string in array
    if (emailAddresses.length === 0) {
      emailAddresses = [''];
    }

    // Build phone numbers array - handle both single and multiple entries
    let phoneNumbers: string[] = [];
    if (request.phones && request.phones.length > 0) {
      // Multiple phone entries - filter out empty strings
      phoneNumbers = request.phones.filter(p => p && p.trim().length > 0);
    } else if (request.phone && request.phone.trim().length > 0) {
      // Single phone entry
      phoneNumbers = [request.phone];
    }
    // If no phones, send empty array
    if (phoneNumbers.length === 0) {
      phoneNumbers = [];
    }

    // Build API request payload
    const payload: CollectabilityAPIRequest = {
      operation: request.searchType,
      company_id: 'CollectABILITY-SingleSearch',
      amount_to_collect: request.amountToCollect?.toString() || '0',
      ai_overview: isScoringOperation, // Only for scoring and scoring_enrich operations
      nameAddresses,
      emailAddresses,
      phoneNumbers,
    };

    logger.info('API payload prepared', {
      nameAddressesCount: nameAddresses.length,
      emailAddressesCount: emailAddresses.length,
      phoneNumbersCount: phoneNumbers.length,
    });

    try {
      const response = await fetch(COLLECTABILITY_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-internal-api-key': COLLECTABILITY_API_KEY,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`CollectABILITY API error: ${response.status} ${response.statusText}`, {
          status: response.status,
          error: errorText,
        });
        throw new NetworkError(
          `API request failed: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`,
          response.status
        );
      }

      const apiResponse: CollectabilityAPIResponse = await response.json();

      // For scoring operations, check if scoring succeeded FIRST before treating as no-hit
      // If scoring succeeded, we proceed normally even if enrichment failed
      const scoringSucceeded = isScoringOperation &&
        apiResponse.scoringResult &&
        apiResponse.scoringResult.status === 'success';

      // Check for complete no-hit scenario (no identity match found AND scoring didn't succeed)
      if (apiResponse.message && !scoringSucceeded) {
        logger.info('No identity match found - complete no-hit', {
          requestId: apiResponse.request_id,
          message: apiResponse.message,
          hadScoringResult: !!apiResponse.scoringResult,
        });
        throw new NoHitError(
          apiResponse.message,
          apiResponse.request_id,
          apiResponse.ai_overview
        );
      }

      // For scoring operations, verify scoring data exists and is successful
      if (isScoringOperation) {
        if (!apiResponse.scoringResult) {
          logger.error('CollectABILITY API response missing scoringResult for scoring operation', apiResponse);
          throw new ProcessingError('Invalid API response - missing scoring data for scoring operation');
        }

        if (apiResponse.scoringResult.status !== 'success') {
          logger.error('CollectABILITY API returned error status for scoring operation', apiResponse);
          throw new ProcessingError('Scoring operation failed - please try again');
        }

        // If we have a message but scoring succeeded, it means enrichment failed for scoring_enrich
        if (apiResponse.message) {
          logger.info('Partial success - scoring succeeded but enrichment failed', {
            requestId: apiResponse.request_id,
            message: apiResponse.message,
            searchType: request.searchType,
          });
        }
      }

      logger.info('CollectABILITY API response received successfully', {
        requestId: apiResponse.request_id,
        searchType: request.searchType,
        hasScore: !!apiResponse.scoringResult,
      });

      // Transform and return result
      return this.transformResponse(apiResponse, request);
    } catch (error) {
      if (error instanceof ValidationError || error instanceof NetworkError || error instanceof ProcessingError || error instanceof NoHitError) {
        throw error;
      }

      logger.error('Unexpected error calling CollectABILITY API:', error);
      throw new NetworkError(
        `Network error calling CollectABILITY API: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }
}

// Export singleton instance
export const collectabilityAPI = new CollectabilityAPIService();
